import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    Grid2 as Grid,
    Box, Container, Paper, useMediaQuery, useTheme
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { AuthLibMSALTokenAcquisitionError, useMSALPayload } from "@initia-digital/auth-client-utils";
import {
    BasicLoader, BottomSearchAppBar, GeneralChip, JobTitleText,
    TextButton, Typography, useSnackbar, Comment,
    Textfield,
    FilledButton
} from "@initia-digital/component-library";
import { baseDefinedAndNotNullNarrowing, BasicLogger } from "@initia-digital/ts-utils";
import { IPlan, IPlanStatus } from "@initia-digital/types/work-and-safety";

import { DEFAULT_CREATE_REVIEW_COMMENT_STATE, useWorkAndSafetyStore } from "../../../../store";
import { CONFIG } from "../../../../config/const";
import { fetchJobDetail, generateAITags } from "../../../../api/query";
import { patchPlan, putPlan, postPlanReviewComment, deletePlanReviewComment} from "../../../../api/mutate";
import { BasicAppBarPage } from "../../../../layouts/BasicAppBarPage";
import { PlanStage } from "../../../../legacy/Plan";
import PlanNavigation from "../../../../legacy/PlanNavigation";
import { useJobSearch, usePlanDetail } from "../../../../hooks";
import { EmergencyDirectiveBanner } from "../../../../legacy/EmergencyDirectiveBanner";

const { PLAN_STEPS, SF_URL } = CONFIG;

const logger = new BasicLogger({ logLevel: import.meta.env.VITE_ENV_LOWER === 'prod' ? 0 : 2, logFormat: 'BROWSER' });

const EditPlanDetail = () => {
    // --------------------------------------------
    const [aiTagGenerationLoading, setAiTagGenerationLoading] = useState<boolean>(false);
    const {
        editPlanState, updateEditPlanState,
        editNextReviewCommentState, setNewReviewComment
    } = useWorkAndSafetyStore();

    const navigate = useNavigate();
    const { jobId, planId } = useParams();
    const [searchParams] = useSearchParams();
    const stage = searchParams.get('stage') ?? PLAN_STEPS[0].value;

    let activeStepIndex = PLAN_STEPS.findIndex(step => step.value === stage);
    activeStepIndex = activeStepIndex !== -1 ? activeStepIndex : 0;
    const previousStepIndex = activeStepIndex > 0 ? activeStepIndex - 1 : PLAN_STEPS.length - 1;
    const nextStepIndex = activeStepIndex < (PLAN_STEPS.length - 1) ? activeStepIndex + 1 : 0;

    // MUI HOOKS
    // ---------------------
    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));
    const isSmallerThanMd = useMediaQuery(theme.breakpoints.down('md'));
    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();
    const searchJob = useJobSearch(authDetail);

    const queryClient = useQueryClient();

    // PLAN DETAIL
    const { planDetailData, planDetailLoading } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));

    // JOB DETAIL
    const { data: jobDetail, isLoading: isJobDetailLoading } = useQuery({
        queryKey: ['job', jobId],
        queryFn: () => fetchJobDetail(authDetail, jobId!),
        enabled: baseDefinedAndNotNullNarrowing(jobId),
        refetchOnWindowFocus: true,
        retry: 2
    });
    const { jobUUID, jobName, client } = jobDetail ?? { jobUUID: undefined, jobName: undefined, client: undefined };

    // MUTATIONS
    // ---------------------
    const updatePlan = useMutation({
        mutationFn: (variables: { planId: string, plan: IPlan, authDetail: Awaited<ReturnType<typeof useMSALPayload>>, showSnackbar?: boolean }) => { return putPlan(variables) },
        onSuccess: (planUpdate, variables) => {
            console.log(planUpdate)
            // Only show snackbar if showSnackbar is true in the variables
            if (variables.showSnackbar !== false) {
                queueSnackbar(`Plan saved`, 'success', 'Success', 3000, '1rem');
            }
            // Also invalidate any plan lists that might be affected
            queryClient.invalidateQueries({ queryKey: ['plan', planUpdate.id] });
            queryClient.invalidateQueries({ queryKey: ['job', planUpdate.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });

            updateEditPlanState({
                ...planUpdate
            });
        },
        onError: (error) => {
            // ----------------------------
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                // --------------------------------------------
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error saving plan', { error });
            }
            logger.error('Error saving plan');
            queueSnackbar('Plan could not be saved', 'error', 'Error saving plan', 3000, '1rem');
        }
    });

    const patchPlanMutation = useMutation({
        mutationFn: (variables: { 
            planId: string, 
            patches: {
                op: "add" | "remove" | "replace" | "move" | "copy" | "test"
                path: string
                value?: unknown
                from?: string
            }[], 
            notify: boolean, 
            authDetail: Awaited<ReturnType<typeof useMSALPayload>>,
            successMessage?: string,
            errorMessage?: string
        }) => {
            return patchPlan({
                planId: variables.planId,
                notify: variables.notify,
                patches: variables.patches,
                authDetail: variables.authDetail
            });
        },
        onSuccess: (planUpdate, variables) => {
            if (variables.successMessage) {
                queueSnackbar(variables.successMessage, 'success', 'Success', 3000, '1rem');
            }
            queryClient.invalidateQueries({ queryKey: ['plan', variables.planId] });
            queryClient.invalidateQueries({ queryKey: ['job', planUpdate.jobId, 'plan'] });
            queryClient.invalidateQueries({ queryKey: ['previousMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['currentMonthPlanList'] });
            queryClient.invalidateQueries({ queryKey: ['nextMonthPlanList'] });
            
            updateEditPlanState({
                ...planUpdate
            });
        },
        onError: (error, variables) => {
            if (
                error instanceof AuthLibMSALTokenAcquisitionError
                || ['Unauthorized', 'Could not get fresh ID token'].includes(error.message)
            ) {
                queueSnackbar(`We need to be sure it's really you. Redirecting you to login`, 'error', 'Unauthorised');
                const { msal } = authDetail;
                msal.instance.loginRedirect({
                    account: msal.accounts.length > 0 ? msal.accounts[0] : undefined,
                    scopes: ["openid", "profile", "User.Read", "User.Read.All", "offline_access"]
                });
            }
            else if (error instanceof Error) {
                logger.error('Error patching plan', { error });
            }
            logger.error('Error patching plan');
            const errorMessage = variables.errorMessage || 'Plan could not be updated';
            queueSnackbar(errorMessage, 'error', 'Error updating plan', 3000, '1rem');
        }
    });

    // EFFECTS
    // Initialize edit plan state from plan detail
    // ---------------------
    useEffect(() => {
        // When you land on the edit-plan page, you want to load the plan detail fresh on all occasions.
        // 1. The jobId or planId has changed (i.e. this is a different plan to what is already held in state)
        if (
            baseDefinedAndNotNullNarrowing(jobId) && baseDefinedAndNotNullNarrowing(planId)
            && planDetailData && !planDetailLoading
        ) {
            // ---------------------
            updateEditPlanState({
                ...planDetailData
            });
        }
    }, [planDetailData, jobId, planId, planDetailLoading, updateEditPlanState]);


    // EVENT HANDLERS
    // ---------------------
    const handleFormNav = (type: 'NEXT' | 'BACK') => {
        // ---------------------
        let stepIdx = activeStepIndex;
        if (type === 'NEXT') {
            stepIdx = nextStepIndex;
        }
        else if (type === 'BACK') {
            stepIdx = previousStepIndex;
        }
        navigate(`/job/${jobId}/edit-plan/${planId}?stage=${PLAN_STEPS[stepIdx]!.value}`);
    }

    const handleSave = async (directPlanProvision?: IPlan) => {
        // ----------------------------------------------
        if (!baseDefinedAndNotNullNarrowing(planId)) {
            throw new Error('Plan ID could not be picked up');
        }

        // DEFAULT behaviour is to use the editPlanState
        let planUpdate = editPlanState;
        if (baseDefinedAndNotNullNarrowing(directPlanProvision)) {
            // -------------------------------------
            planUpdate = directPlanProvision;
        }

        await updatePlan.mutateAsync({
            planId,
            plan: {
                ...planUpdate,
                jobId
            },
            authDetail,
            showSnackbar: true
        });
    }

    const handleSaveAndNext = async () => {
        // ----------------------------------------------

        if (!baseDefinedAndNotNullNarrowing(planId)) {
            throw new Error('Plan ID could not be picked up');
        }

        const res = await updatePlan.mutateAsync({
            planId,
            plan: {
                ...editPlanState,
                jobId
            },
            authDetail,
            showSnackbar: true
        });

        navigate(`/job/${jobId}/edit-plan/${res.id}?stage=${PLAN_STEPS[nextStepIndex]!.value}`);
    }

    const handleStatusChange = async (planId: string, status: IPlanStatus, notify: boolean) => {
        // ----------------------------------------------
        await patchPlanMutation.mutateAsync({
            planId,
            patches: [
                {
                    op: 'replace',
                    path: '/status',
                    value: status
                }
            ],
            notify,
            authDetail,
            successMessage: `Plan status updated to ${status}`,
            errorMessage: 'Plan status could not be updated'
        });
    }

    const handleAiTagGeneration = async () => {
        // --------------------------------------
        setAiTagGenerationLoading(true);
        const res = await generateAITags(authDetail, {
            maxTags: 5,
            outputSpec: encodeURIComponent(`specific type of geotechnical work being done in this description`),
            temperature: 0.3
        },
            {
                content: editPlanState.description
            }
        );

        if ('tags' in res && baseDefinedAndNotNullNarrowing(res?.tags) && res.tags.length > 0) {
            // ---------------------------------------------
            const uniqueTags = res.tags.filter((tag: string) => editPlanState.objectives.indexOf(tag) === -1);
            updateEditPlanState({
                ...editPlanState,
                objectives: [...editPlanState.objectives, ...uniqueTags]
            });
        }
        setAiTagGenerationLoading(false);
    }


    return (
        <BasicAppBarPage bgColor={"white"}>
            <Container maxWidth="xl" sx={{ px: !isSmallerThanMd ? "25px" : "0px" }}>
                <EmergencyDirectiveBanner />
                <Box mx="10px" my="5px" height="100%">
                    <Paper sx={{ height: '100%', width: '100%' }} elevation={0}>
                        <Box
                            display="flex" flexDirection="row" justifyContent="center"
                            pt={{ xs: '10px', sm: '15px', md: '20px' }}
                            px={{ xs: '5px', md: '20px' }}
                            height="100%"
                        >
                            <Box display="flex" flexDirection="column" sx={{ width: '100%' }} mb="20px" >
                                <Grid container direction="row" justifyContent="center" alignItems="stretch" spacing={2}>
                                    <Grid size={{ xs: 12, lg: 3 }} alignSelf={"flex-start"}>
                                        <Box display="flex" flexDirection="column">
                                            <Box>
                                                <Box display="flex" mb="10px">
                                                    <TextButton
                                                        color={theme.palette.primary.contrastText}
                                                        hoverBackgroundColor={theme.palette.primary.main}
                                                        onClick={() => {
                                                            navigate(`/job/${jobId}`)
                                                        }}
                                                        startIcon={<ArrowBackIcon />}
                                                    >
                                                        {`Back to ${jobId}`}
                                                    </TextButton>
                                                </Box>
                                                {
                                                    isJobDetailLoading
                                                    &&
                                                    <BasicLoader
                                                        size={50}
                                                    />
                                                }
                                                {
                                                    baseDefinedAndNotNullNarrowing(jobDetail)
                                                    &&
                                                    <Box
                                                        px="20px"
                                                        display="flex"
                                                        flexDirection="column"
                                                    >
                                                        <JobTitleText
                                                            jobId={jobId}
                                                            jobName={jobName ?? '...'}
                                                            isSelected={true}
                                                        />
                                                        <Box>
                                                            <Typography
                                                                fontSize="0.8rem"
                                                                fontWeight={300}
                                                                color={theme.palette.primary.contrastText}
                                                            >
                                                                {client}
                                                            </Typography>
                                                        </Box>
                                                        <Box py="5px">
                                                            <GeneralChip
                                                                label="Open in Salesforce"
                                                                href={`${SF_URL}/${jobUUID}`}
                                                                size="vsmall"
                                                                backgroundColor={"#3bbff7"}
                                                                color="white"
                                                                clickable
                                                                newTab={true}
                                                            />
                                                        </Box>
                                                    </Box>
                                                }
                                                <PlanNavigation
                                                    stage={stage}
                                                    handleFormNav={handleFormNav}
                                                />
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, lg: 5 }} alignSelf={"flex-start"}>
                                        <Box display="flex" justifyContent="center">
                                            <PlanStage
                                                handleSave={handleSave}
                                                handleSaveAndNext={handleSaveAndNext}
                                                handleStatusChange={handleStatusChange}
                                                aiTagGenerationLoading={aiTagGenerationLoading}
                                                setAiTagGenerationLoading={setAiTagGenerationLoading}
                                                handleAiTagGeneration={handleAiTagGeneration}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12, lg: 4 }} alignSelf={"flex-start"}>
                                        <>
                                            <Box
                                                display="flex" flexDirection="column" width="100%"
                                                px="10px"
                                            >
                                                <Typography
                                                    variant="body1" fontSize={isSmallerThanSm ? "0.7rem" : "1rem"}
                                                    color={theme.palette.secondary.main} fontWeight={700}
                                                >
                                                    Review feedback
                                                </Typography>
                                            </Box>
                                            {
                                                planDetailLoading && (
                                                    <Box
                                                        display="flex" flexDirection="column" width="100%"
                                                        px="10px"
                                                    >
                                                        <BasicLoader size={50} />
                                                    </Box>
                                                )
                                            }
                                            {
                                                !planDetailLoading
                                                && baseDefinedAndNotNullNarrowing(editPlanState)
                                                && baseDefinedAndNotNullNarrowing(editPlanState.reviewNotes)
                                                && editPlanState.reviewNotes.length > 0 ? (
                                                    <Box
                                                        display="flex" flexDirection="column" width="100%"
                                                        px="10px"
                                                    >
                                                        {
                                                            [...editPlanState.reviewNotes]
                                                                .sort((a, b) => DateTime.fromISO(a.lastUpdateTimestamp || a.creationTimestamp!).toMillis() - DateTime.fromISO(b.lastUpdateTimestamp || b.creationTimestamp!).toMillis())
                                                                .map((note) => baseDefinedAndNotNullNarrowing(note?.lastUpdateTimestamp) ? (
                                                                    <Box
                                                                        key={`review-note-${note.id}`}
                                                                        my="5px"
                                                                        whiteSpace={"nowrap"}
                                                                    >
                                                                        <Comment
                                                                            message={note.notes}
                                                                            timestamp={note.lastUpdateTimestamp}
                                                                            userName={note.reviewedBy}
                                                                            userAvatar={note.reviewedByIcon ?? undefined}
                                                                            messageFontSize={'0.65rem'}
                                                                            userNameFontSize={'0.65rem'}
                                                                            timestampFontSize={'0.7rem'}
                                                                            handleDelete={async () => {
                                                                                // ---------------------
                                                                                await deletePlanReviewComment({
                                                                                    jobId: jobId!,
                                                                                    planId: planId!,
                                                                                    reviewNoteId: note.id,
                                                                                    authDetail
                                                                                });
                        
                                                                                await handleSave();                        
                                                                                await queryClient.invalidateQueries({ queryKey: ['plan', planId] });
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ) : <></>)
                                                        }
                                                    </Box>
                                                )
                                                :
                                                <Box
                                                    display="flex" flexDirection="column" width="100%"
                                                    px="10px"
                                                >
                                                    <Typography
                                                        fontSize="0.8rem"
                                                        fontWeight={300}
                                                        color={theme.palette.primary.contrastText}
                                                    >
                                                        Get started and pass on some feedback
                                                    </Typography>
                                                </Box>
                                            }
                                            <Box
                                                display="flex" flexDirection="column" width="100%"
                                                my="10px" px="10px"
                                            >
                                                <Textfield
                                                    fontSize="0.8rem"
                                                    name="reviewComment"
                                                    label="Add a review comment"
                                                    placeholder="Add a review comment"
                                                    value={editNextReviewCommentState}
                                                    minRows={3}
                                                    handleChange={(event) => {
                                                        // ---------------------
                                                        setNewReviewComment(event.target.value);
                                                    }}
                                                />
                                            </Box>
                                            <Box
                                                display="flex"
                                                justifyContent="flex-end"
                                                width="100%"
                                                px="10px"
                                                mt="10px"
                                            >
                                                <FilledButton
                                                    size="small"
                                                    startIcon={<SendIcon />}
                                                    color={theme.palette.secondary.contrastText}
                                                    backgroundColor={theme.palette.secondary.main}
                                                    onClick={async () => {
                                                        // ---------------------
                                                        await postPlanReviewComment({
                                                            jobId: jobId!,
                                                            planId: planId!,
                                                            reviewComment: editNextReviewCommentState,
                                                            authDetail
                                                        });

                                                        await handleSave();
                                                        setNewReviewComment(DEFAULT_CREATE_REVIEW_COMMENT_STATE);

                                                        await queryClient.invalidateQueries({ queryKey: ['plan', planId] });
                                                    }}
                                                >
                                                    Post
                                                </FilledButton>
                                            </Box>
                                        </>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
                <BottomSearchAppBar
                    placeholder='Search for a job'
                    label='Search for a Job'
                    labelKey='jobId'
                    secondaryKeysForDisplay={['jobId']}
                    suggestionKeysForOptionDisplay={['jobId', 'jobName']}
                    suggestionKeysToSearchOption={['jobId', 'jobName']}
                    uniqueOptionKeyProps={['jobId']}
                    handleChange={(value) => { navigate(`/job/${value.jobId}`) }}
                    searchSuggestion={searchJob}
                />
            </Container>
        </BasicAppBarPage>
    )
}

export default EditPlanDetail;